import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Observable, of} from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { IExportRequest, IOrderExecutionHistory, IOrders, IOrdersSupportDetails, IOrderStatusHistory, IUpdateContactNumbers, IValidateThinkPipeExport } from '@irb/irb-orders/irb-orders.model';
import {
    OrderValidator,
    UpdateOrder,
    UpdateOrderPayload
} from "@irb/irb-orders/order-blotter/inventory/update-order-payload";
import {formatOrderStatusReverse, formatTransactionReverse} from "@irb-common/formatters";
import { IMasterAccountAccessResponse } from '@irb/irb-orders/irb-orders.model';

// /irebal/blotter/updateOrder
//{"orderID":1270440,"versionId":0,"orderAction":"NOTHELD","orderStatus":"APPROVED","ordIsNotHeld":false,"orderType":"LIMIT","expirationType":"DAY","defaultOrderPriceType":"Bid","price":130.93}
//{"orderID":1270440,"versionId":1,"orderAction":"NOTHELD","orderStatus":"APPROVED","ordIsNotHeld":true,"orderType":"MARKET","expirationType":"DAY","expirationDate":null,"notHeldDestination":"AOM_PRINTER","userContactNo":"7828285932"}


@Injectable({ providedIn: 'root' })
export class InventoryService {
    _filterCriteria: string;
    _sortCriteria: string = '';
    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type':  'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
        })
    };
    isBlockOrder = false;
    isArchivedOrders = false;
    blockingConditionExists = false;
    progressBarValue = -1;
    isArchivedOrdersPrevious = false;
    updateOrderPayload: UpdateOrderPayload;
    openEditMode = false;

    constructor(private readonly http: HttpClient) {
        this.init();
    }

    init() {
        this.updateOrderPayload = new UpdateOrderPayload();
        this.updateOrderPayload.data = new UpdateOrder();
        this.updateOrderPayload.prevData = new UpdateOrder();
        this.updateOrderPayload.validator = new OrderValidator();
        this.updateOrderPayload.isModified = false;
    }

    getOrders(params: any, current:string): Observable<HttpResponse<IOrders[]>> {
        let sort = this.getSortCriteria(params);
        let filterKeys = this.getFilterCriteria(params);

        const headers = new HttpHeaders({
            'X-HTTP-Method-Override':'GET',
            'Filter-Item':'items=' + params.request.startRow + '-' + (--params.request.endRow)
          });
        
        //let url = '/irebalone/blotter/'+ current;
        let url = '/irebalone/blotter/';
        
        if(current == 'activeOrders'){    
		  url = url+'active-orders';
		} else if(current == 'resolvedOrders'){
          url = url  + 'resolved-orders';
        } else if(current == 'automatedOrders') {
        	url = url  + 'automated-orders';
        }else{
           url = url  + current;
        }
        
        if (current === 'resolvedOrders') {
            url += '/' + this.isArchivedOrders;
        }

        return this.http.post<IOrders[]>(url +'/?' + sort,{Filter:filterKeys},{ headers: headers, observe: 'response' });
    }

    getFilterCriteria(params: any) {
        this.isArchivedOrders = false;
        this.isBlockOrder = false;
        let filterKeys = '';
        
        if (params.request.filterModel && params.request.filterModel['accountNumber']) {
            if (params.request.filterModel['accountNumber'].filter) {
                filterKeys = filterKeys + '%26accountNumber%3D' + encodeURIComponent(params.request.filterModel['accountNumber'].filter);
            }
        }

        if (params.request.filterModel && params.request.filterModel['accountName']) {
            if (params.request.filterModel['accountName'].filter) {
                filterKeys = filterKeys + '%26accountName%3D' + encodeURIComponent(params.request.filterModel['accountName'].filter);
            }
        }

        if (params.request.filterModel && params.request.filterModel['securitySymbol']) {
            if (params.request.filterModel['securitySymbol'].filter) {
                filterKeys = filterKeys + '%26securitySymbol%3D' + encodeURIComponent(params.request.filterModel['securitySymbol']
                    .filter.toUpperCase());
            }
        }

        if (params.request.filterModel && params.request.filterModel['veoOrderID']) {
            if (params.request.filterModel['veoOrderID'].filter) {
                filterKeys = filterKeys + '%26veoOrderID%3D' + encodeURIComponent(params.request.filterModel['veoOrderID'].filter);
            }
        }

        if (params.request.filterModel && params.request.filterModel['transactionType']) {
            if (params.request.filterModel['transactionType'].values) {
                filterKeys = filterKeys + '%26transactionType%3D' + params.request.filterModel['transactionType'].values
                    .map((value:string) => formatTransactionReverse(value));
            }
        }

        if (params.request.filterModel && params.request.filterModel['securityType']) {
            if (params.request.filterModel['securityType'].values) {
                filterKeys = filterKeys + '%26securityType%3D' + params.request.filterModel['securityType'].values;
            }
        }

        if (params.request.filterModel && params.request.filterModel['ticketType']) {
            if (params.request.filterModel['ticketType'].values) {
                filterKeys = filterKeys + '%26ticketType%3D' + params.request.filterModel['ticketType'].values;
            }
        }

        if (params.request.filterModel && params.request.filterModel['createdDate']) {
            const fromDate = params.request.filterModel['createdDate'].filterFrom;
            const toDate = params.request.filterModel['createdDate'].filterTo;
            this.isArchivedOrders = params.request.filterModel['createdDate'].isArchive;
            const filterValue = params.request.filterModel['createdDate'].type + ',' + fromDate + ',' + toDate;
            filterKeys = filterKeys + '%26createdDate%3D' + filterValue;
        }

        if (params.request.filterModel && params.request.filterModel['modifiedDate']) {
            const fromDate = params.request.filterModel['modifiedDate'].filterFrom;
            const toDate = params.request.filterModel['modifiedDate'].filterTo;
            this.isArchivedOrders = params.request.filterModel['modifiedDate'].isArchive;
            const filterValue = params.request.filterModel['modifiedDate'].type + ',' + fromDate + ',' + toDate;
            filterKeys = filterKeys + '%26modifiedDate%3D' + filterValue;
        }

        if (params.request.filterModel && params.request.filterModel['rebalanceResultId']) {
            if (params.request.filterModel['rebalanceResultId'].filter) {
                filterKeys = filterKeys + '%26rebalanceResultId%3D' + encodeURIComponent(params.request.filterModel['rebalanceResultId'].filter);
            }
        }

        if (params.request.filterModel && params.request.filterModel['portfolioName']) {
            if (params.request.filterModel['portfolioName'].filter) {
                filterKeys = filterKeys + '%26portfolioName%3D' + encodeURIComponent(params.request.filterModel['portfolioName'].filter);
            }
        }

        if (params.request.filterModel && params.request.filterModel['masterAccount']) {
            if (params.request.filterModel['masterAccount'].values) {
                filterKeys = filterKeys + '%26masterAccount%3D' + params.request.filterModel['masterAccount'].values;
            }
        }

        if (params.request.filterModel && params.request.filterModel['submittedBy']) {
            if (params.request.filterModel['submittedBy'].values) {
                filterKeys = filterKeys + '%26submittedBy%3D' + params.request.filterModel['submittedBy'].values;
            }
        }
       
        if (params.request.filterModel && params.request.filterModel['extAccOnly']) {
            if (params.request.filterModel['extAccOnly'].values) {
                filterKeys = filterKeys + '%26externalAccountsOnly%3D1'
            }
        }

        if (params.request.filterModel && params.request.filterModel['blockOrders']) {
            if (params.request.filterModel['blockOrders'].values) {
                this.isBlockOrder = true;
                filterKeys = filterKeys + '%26selectingIndividual%3Dfalse';
            }
        }

       // if (!this.isBlockOrder) { // no order status filters for block orders
            if (params.request.filterModel && params.request.filterModel['orderStatus']) {
                if (params.request.filterModel['orderStatus'].values) {
                    filterKeys = filterKeys + '%26orderStatus%3D' + params.request.filterModel['orderStatus'].values
                        .map((value: string) => formatOrderStatusReverse(value));
                }
            }
        //}

        if (filterKeys) {
            filterKeys = '&Filter=' + filterKeys;
            this._filterCriteria = filterKeys;
        } else {
            this._filterCriteria = '';
        }

        return filterKeys;
    }

    getSortCriteria(params: any): string {
        let sort = '';

        if (params.request.sortModel.length > 0) {
            let sortColumn = params.request.sortModel[0].colId;
            const sortOrder = (params.request.sortModel[0].sort === 'asc') ? '+' : '-';

            sort = 'sort(' + sortOrder + sortColumn + ')';
            this._sortCriteria = 'sort(' + sortOrder + sortColumn + ')';
        }

        return sort;
    }

    clearCriteria() {
        this._filterCriteria = '';
        this._sortCriteria = '';
    }

    getOrdersSupportDetails(current:string): Observable<IOrdersSupportDetails> {
        const isArchivedOrders = (current === 'resolvedOrders' && this.isArchivedOrders);
        return this.http.get<IOrdersSupportDetails>('/irebalone/blotter/orders-support-details/'+current+'/' + isArchivedOrders).pipe(
          delay(1000), tap( res => {
              if (res && res.blockingConditionExists) {
                  this.blockingConditionExists = true;
              } else {
                  this.blockingConditionExists = false;
              }
          }) );
    }

    exportOrders(data: IExportRequest):Observable<any>{
        const isArchivedOrders = (data.currentTab === 'resolvedOrders' && this.isArchivedOrders);
        const _filterCriteria = this._filterCriteria?this._filterCriteria:'';
        return this.http.post("/irebalone/blotter-action/async/export-orders/"+ data.currentTab +"/" + isArchivedOrders+"?"+_filterCriteria, data);
     }

     exportAllocations(data: IExportRequest):Observable<any>{
         const isArchivedOrders = (data.currentTab === 'resolvedOrders' && this.isArchivedOrders);
         const _filterCriteria = this._filterCriteria?this._filterCriteria:'';
         return this.http.post("/irebalone/blotter-action/async/export-allocations/"+ data.currentTab +"/" + isArchivedOrders+"?"+_filterCriteria, data);
     }

     exportBlockAllocations(data: IExportRequest):Observable<any>{
         const isArchivedOrders = (data.currentTab === 'resolvedOrders' && this.isArchivedOrders);
         const _filterCriteria = this._filterCriteria?this._filterCriteria:'';
         return this.http.post("/irebalone/blotter-action/async/export-block-allocations/"+ data.currentTab +"/" + isArchivedOrders+"?"+_filterCriteria, data);
     }
     getOrdersByFilter(data: any): Observable<any> {
         const isArchivedOrders = (data.currentTab === 'resolvedOrders' && this.isArchivedOrders);
         const _filterCriteria = this._filterCriteria?this._filterCriteria:'';
        return this.http.post('/irebalone/blotter/get-orders-by-filter/'+data.current+'/' + isArchivedOrders + '/?'+ _filterCriteria,data);
     }

     exportThinkPipe(data: IExportRequest):Observable<any>{
        const _filterCriteria = this._filterCriteria?this._filterCriteria:'';
        return this.http.post("/irebalone/blotter-action/async/export-orders-to-think-pipes-format?"+_filterCriteria, data);
    }

    getOrderTresholdLimits(): Observable<any> {
        return this.http.get('/irebalone/blotter/get-order-treshold-limits/');
    }

    updateOrderStatus(data: any): Observable<any> {
        return this.http.post<any>('/irebalone/blotter-action/update-order-status/', data);
    }

    getSelectedOrderCount(data: any): Observable<any> {
        return this.http.post<any>('/irebalone/blotter-action/get-selected-orders-count/', data);
    }

    updateOrder(data: any): Observable<any> {
        return this.http.post<any>('/irebalone/blotter-action/update-order/', data);
    }

    checkForSubmitWarnings(data: any): Observable<any> {
        return this.http.post<any>('/irebalone/blotter/check-for-submit-warnings/', data);
    }

    validateExportOrdersForThinkPipesFormat(request: IExportRequest):Observable<IValidateThinkPipeExport>{
        return this.http.post("/irebalone/blotter-action/validate-export-orders", request);
    }
    
    /*updateOrdersBlotter(data: any): Observable<any> {
        return this.http.post<any>('/irebalone/blotter-action/update-order/', data);
    }*/
        
    getComponentOrders(orderId:number, current?: string, createdDate?: any, isArchivedOrders?: boolean):Observable<any>{
        let isArchivedDatesFlag = false;
        if (!current) {
            current = 'activeOrders';
        }
        if (current === 'resolvedOrders') {
          if (isArchivedOrders === undefined) {
            const currentDateTime = new Date().getTime();
            if (createdDate) {
                if ((currentDateTime-createdDate)/ (1000 * 60 * 60 * 24) > 30) {
                    isArchivedDatesFlag = true;
                }
            }
          } else {
            isArchivedDatesFlag = isArchivedOrders;
          }
        }
        return this.http.get<any>('/irebalone/block-action/get-component-orders/' + current + '/' + isArchivedDatesFlag + '/?orderId='+orderId);
    }
    getOrderStatusCountWithFilter(data: any): Observable<any> {
        const isArchivedOrders = (data.currentTab === 'resolvedOrders' && this.isArchivedOrders);
        const _filterCriteria = this._filterCriteria?this._filterCriteria:'';
       return this.http.post('/irebalone/blotter/get-order-status-count-with-filter/'+data.current+'/' + isArchivedOrders + '/?'+ _filterCriteria,data);
    }
    updateAsynchOrderStatus(data: any): Observable<any> {
        const isArchivedOrders = (data.currentTab === 'resolvedOrders' && this.isArchivedOrders);
        const _filterCriteria = this._filterCriteria?this._filterCriteria:'';
       return this.http.post('/irebalone/blotter-action/update-asynch-order-status/?'+ _filterCriteria,data);
    }
    getOrderStatusUpdate(ordStatUpdId:number):Observable<any>{
        return this.http.get<any>('/irebalone/blotter-action/get-order-status-update/'+ordStatUpdId);
    }

    getOrderStatusHistory(orderId:number,currentTab:string):Observable<IOrderStatusHistory[]>{
        return this.http.get<any>('/irebalone/blotter-action/get-order-status-history/'+currentTab+'/'+ this.isArchivedOrders+'/'+orderId);
    }

    getOrderExecutionHistory(orderId:number,currentTab:string):Observable<IOrderExecutionHistory>{
     return this.http.get<any>('/irebalone/blotter-action/get-order-execution-history/'+currentTab+'/'+this.isArchivedOrders+'/'+orderId+'/false');
    }

    getBidAndAskPrices(symbol: string, securityType: string) {
        if(securityType == 'Option'){
            symbol = symbol.replace(new RegExp("/", 'g'),","); 
        }
     return this.http.get<any>('/irebalone/blotter-action/get-bid-and-ask-prices?symbol=' + encodeURIComponent(symbol)+ '&secType=' + securityType);
    }
    getPermissions() {
        return this.http.get<any>('/irebalone/blotter/get-permissions');
    }
    getIsOptionTradeEnabled() {
        return this.http.get<any>('/irebalone/blotter/get-is-option-trade-enabled');
    }
    
    validateImportData(data:any): Observable<any> {
         return this.http.post('/irebalone/blotter-action/validate-import-orders',data,{ responseType: 'text' as 'json' });    
    }

    insertImportedOrders(data: any): Observable<any> { 
        return this.http.post("/irebalone/blotter-action/insert-imported-orders", data, this.httpOptions);
    }

    saveTempSecurities(data: any): Observable<any> { 
        return this.http.post("/irebalone/blotter-action/save-temp-securities", data, this.httpOptions);
    }
    updateMultipleOrders(data: any): Observable<any> {
        return this.http.post<any>('/irebalone/blotter-action/update-multiple-orders/', data);
    }

    updateContactNumbers(data: IUpdateContactNumbers): Observable<any> {
        return this.http.post<any>('/irebalone/blotter-action/update-contact-numbers/', data);
    }

    getContactNumbers() {
        return this.http.get<any>('/irebalone/blotter/get-contact-numbers');
    }

    updateMultipleOrdersStatus(statusId: string): Observable<Object> {
        return this.http.get<any>('/irebalone/blotter-action/update-multiple-orders-status/' + encodeURIComponent(statusId), this.httpOptions);
    }

    validateImportDataAsync(data:any): Observable<any> {
        return this.http.post('/irebalone/blotter-action/async/validate-import-orders', data);
    }
   
    saveTempSecuritiesAsync(data: any): Observable<any> { 
        return this.http.post("/irebalone/blotter-action/async/save-temp-securities", data, this.httpOptions);
    }

    validateImportOrdersAsyncStatus(statusId: string): Observable<Object> {
        if (!/^[0-9]+$/.test(statusId)) {
            throw new Error("Invalid character payload.");
        } else {
            const e = encodeURIComponent(statusId);
            const u = "/irebalone/blotter-action/order-import-validate-status/*".replace("*",e);
            return this.http.get<any>(u, this.httpOptions).pipe(delay(1000));
        }    
    }

    saveTempSecAsyncStatus(statusId: string): Observable<Object> {
        return this.http.get<any>('/irebalone/blotter-action/order-import-save-sec-status/' + encodeURIComponent(statusId), this.httpOptions);
    }

    insertImportOrdersAsyncStatus(statusId: string): Observable<Object> {
        return this.http.get<any>('/irebalone/blotter-action/order-import-insert-status/' + encodeURIComponent(statusId), this.httpOptions);
    }

    insertImportedOrdersAsync(data: any, currentTab: string): Observable<any> {
        return this.http.post("/irebalone/blotter-action/insert-imported-orders-async/" + currentTab, data);
        // false && console.log(data);
        // return of({
        //     "responseStatus": "SUCCESS",
        //     "responseData": null,
        //     "responseUtilityMap": {
        //         "reqStatus": "IN_PROGRESS",
        //         "statusId": 61
        //     }
        // });
    }

    actionStatusCheck(actionStatusId: any) {
        return this.http.get('/irebalone/blotter-action/get-order-blotter-status/' + actionStatusId).pipe(delay(1000));
        // false && console.log(actionStatusId);
        // return of({
        //     "responseStatus": "SUCCESS",
        //     "responseData": {
        //         "statusId": 61,
        //         "action": "MODEL_IMPORT",
        //         "moduleName": "MODEL_MODULE",
        //         "reqStatus": "COMPLETED",
        //         "envId": 74830353,
        //         "recievedCount": 2,
        //         "successRecCount": 1,
        //         "failedRecCount": 0,
        //         "failMsg": "StaleDataException",
        //         "requestedBy": "QA_Test123",
        //         "requestedDate": 1688668614706,
        //         "lastModifiedBy": "QA_Test123",
        //         "lastModifiedDate": 1688668615817
        //     },
        //     "responseUtilityMap": null
        // });
    }

    // getGlobalSettings(): Observable<Object> {
    //     return this.http.get<any>('/irebalone/settings/get-global-settings');
    // }

    getMasterAccountsAccessForOrderBlotter(_postData: any): Observable<IMasterAccountAccessResponse> {
        _postData.filter = this._filterCriteria;  
        return this.http.post<IMasterAccountAccessResponse>('/irebalone/blotter/check-master-access-for-order-blotter', _postData, this.httpOptions);
        // return of({
        //   "responseStatus": "SUCCESS",
        //   "responseData": {
        //       "isMasterAccountsAccessChanged": true,
        //       "lostMaster": [1000,2000,3000,4000,5000,6000,7000,8000,9000],
        //       "noMasterForSelection": true
        //   },
        //   "responseUtilityMap": null
        // });
    }    
}
